import type {
  LbEventVarInfo,
  LbPart,
  LbPartData,
  LbPost,
  LbPostData,
  LbPostEvent,
  LbPostEventOld,
  LbUpdate,
} from '../types';
import { LbEventType, VarReason } from '../types';
import * as utils from '../utils';
import logger from '../utils/logger';

import { getEventKey, eventTypeMapping, eventPhaseMapping } from './event-mapping';
import { varInfoMapping } from './var-mapping';

export const ComposerTypes = {
  HEADING: 'HEADING',
  PARAGRAPH: 'PARAGRAPH',
} as const;
export type ComposerTypes = (typeof ComposerTypes)[keyof typeof ComposerTypes];

export const postProcessPostEvent = (originalEvent: LbPostEventOld): LbPostEvent => {
  if (!originalEvent) return null;

  const newEvent = { ...originalEvent } as LbPostEvent;

  const key = getEventKey(originalEvent);
  const eventTypeMap = eventTypeMapping[key];
  if (eventTypeMap) {
    const [eventType, eventSubType, eventExtraSubType] = eventTypeMap;
    newEvent.eventType = eventType;
    newEvent.eventSubType = eventSubType;
    newEvent.eventExtraSubType = eventExtraSubType;
  }
  newEvent.eventPhase = eventPhaseMapping[originalEvent.eventPhase] ?? originalEvent.eventPhase;

  return newEvent;
};

export const postProcessVarInfo = (originalEvent: LbPostEventOld): LbEventVarInfo => {
  if (!originalEvent) return null;

  const varInfoMap = varInfoMapping[`${originalEvent.eventSubType}_${originalEvent.eventExtraSubType ?? 0}`];
  if (varInfoMap) {
    const [status, incident, result] = varInfoMap;
    return {
      status,
      incident,
      result,
    };
  }
};

export const postProcessPostData = (postData: LbPostData): LbPostData => {
  if (!postData) return null;

  const newPostData = { ...postData };
  newPostData.lbPostFSPEvent ??= postProcessPostEvent(postData.lbPostEvent);
  if (newPostData.lbPostFSPEvent?.eventType === LbEventType.VAR) {
    newPostData.lbPostFSPEvent.varInfo ??= postProcessVarInfo(postData.lbPostEvent);
  }

  return newPostData;
};

export const getPart = (nodes: any[], liveblogPostData: LbPostData, baseImgUrl: string) => (part) => {
  try {
    const ret: LbPart = {
      data: {} as LbPartData,
      ...utils.getDataSource(part, liveblogPostData),
    };

    const isArticle = part._attributes?.['emk-class'] === 'article';

    switch (ret.datasource) {
      case 'UefaEditorial':
        ret.data.Title = utils.stripHtml(
          !isArticle
            ? part._html
            : (nodes?.[ret.dataId]?.files?.['content.json']?.data?.document?.headgroup?.find(
                (s) => s._type === 'ALTERNATE-HEADLINE',
              )?._html ?? part._html),
        );

        if (nodes?.[ret?.dataId]) {
          const topMedia = nodes[ret.dataId]?.files?.['content.json']?.data?.document?.mediagroup[0];
          const image = topMedia ? utils.getImageObject(topMedia?.figure[0], baseImgUrl) : null;
          ret.data.Id = ret.dataId;

          const alternateSummary = nodes[ret.dataId]?.files?.['content.json']?.data?.document?._elements?.find(
            (s) => s._type === 'ALTERNATE-SUMMARY',
          )?._html;
          ret.data.Description = isArticle && alternateSummary ? alternateSummary : nodes[ret.dataId]?.description;
          ret.data.Image = image;
          ret.data.ImageUrl = image?.wide?.w1;
          ret.data.Editorial = nodes[ret.dataId];
          ret.data.HlsUrl = nodes[ret.dataId]?.attributes?.mediaInfo?.mediaUrl;
          ret.data.HideFromWeb = utils.getHideFromWeb(nodes[ret.dataId]);
          ret.data.Section = utils.getSection(nodes[ret.dataId]);
          ret.data.Kind = utils.getKind(nodes[ret.dataId]);
          ret.data.Url = utils.getUrl(nodes[ret.dataId]);
        }
        ret.resourceId = utils.getResourceId(part);
        ret.data.MediaType = part?._attributes?.['emk-class'];
        break;
      case 'Poll':
        ret.data.Id = part?._attributes?.['data-id'];
        break;
      case 'Card':
        ret.data.Id = part?._attributes?.['emk-data-id'];
        ret.data.Component = part?._attributes?.['emk-data-component'];
        ret.data.Type = part?._attributes?.['emk-type'];
        break;
      case 'Social':
        ret.data.Id = part?._attributes?.['emk-postid'];
        ret.data.Type = part?._attributes?.['emk-type'];
        ret.data.Url = part?._attributes?.['emk-posturl'];
        break;
      case 'MatchSummary':
        ret.data.Id = liveblogPostData?.lbPostFSPEvent.idMatch;
        ret.data.Type = part._type !== ComposerTypes.HEADING ? liveblogPostData?.lbPostType : part._type;
        break;
      case 'MatchEvents':
        ret.data.Type = liveblogPostData?.lbPostType;
        break;
      case 'VideoPost':
        ret.data.Id = part?._attributes?.['data-id'];
        ret.data.ForeignId = part?._attributes?.['href'].replace('cobaltextref:', '');
        break;
      case 'FreePhoto':
        if (part?.figure && part?.figure[0]?.img[0]?.src) {
          const image = utils.getImageObject(part.figure[0], baseImgUrl);
          ret.data.image = image;
          ret.data.imgUrl = image?.free?.f1;
          ret.data.imgWidth = part.figure[0]?.img[0]?.['emk-width'];
          ret.data.imgHeight = part.figure[0]?.img[0]?.['emk-height'];
        }
        break;
    }
    ret.data.Text = part._html;
    return ret;
  } catch (e) {
    logger.error('Error processing part', part, nodes, liveblogPostData, e);
    return null;
  }
};

export const getParts = (post, baseImgUrl: string): LbPart[] =>
  post?.attributes?.['content.json']?.data?.article?._elements
    ?.map(getPart(post.attributes.nodes, postProcessPostData(post?.attributes?.liveblogPostData), baseImgUrl))
    ?.filter(Boolean) ?? [];

export const postProcessPost =
  (baseImgUrl: string) =>
  (post: any): LbPost => {
    try {
      return {
        id: post?.id,
        date: post?.attributes?.created,
        lastUpdateDate: post?.attributes?.eventTime,
        liveblogPostData: postProcessPostData(post?.attributes?.liveblogPostData),
        sponsors: post?.attributes?.lbPostSponsors?.map((s) => ({
          code: s.lbPostSponsorCode,
        })),
        reactions: post?.reactions,
        author: { name: post?.attributes?.creator },
        lastModifier: { name: post?.attributes?.last_modifier },
        parts: getParts(post, baseImgUrl),
      };
    } catch (e) {
      logger.error('Error processing post', post, e);
      return null;
    }
  };

export const postProcessUpdate =
  (baseImgUrl: string) =>
  (update: any): LbUpdate => {
    try {
      return {
        postId: update.post?.id,
        body: postProcessPost(baseImgUrl)(update.post),
        action: update.operation,
      };
    } catch (e) {
      logger.error('Error processing update', update, e);
      return null;
    }
  };

export const hideFromWeb = (item: LbPost): boolean => item?.parts?.some((p) => p?.data?.HideFromWeb === true);
export const noReasonVar = (item: LbPost): boolean =>
  item?.liveblogPostData?.lbPostFSPEvent?.eventType === LbEventType.VAR &&
  item?.liveblogPostData?.lbPostFSPEvent?.varInfo?.reason === VarReason.NONE;
